import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import { InternalCuratedSearchFilters } from "../../../__generated__/graphql";
import { useContext, useState } from "react";
import CuratedSearchFilterCurrentCompanySection from "./CuratedSearchFilterSection/CuratedSearchFilterCurrentCompanySection";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import { toast } from "react-hot-toast";
import { hasFiltersApplied } from "../curatedSearchUtils";
import { SparklesIcon } from "@heroicons/react/20/solid";
import CuratedSearchCiroInternalIndustrySection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratesSearchCiroInternalIndustrySection";
import CuratedSearchCiroInternalGeographySection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalGeographySection";
import CuratedSearchCiroInternalCompanyHqSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalCompanyHqSection";
import CuratedSearchCiroInternalCompanyHeadCountSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalCompanyHeadCountSection";
import {
  AdjustmentsVerticalIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import CuratedSearchCiroInternalCompanyKeyWordsSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalCompanyKeyWordsSection";
import isValidBooleanExpression from "../../../utils/isValidBooleanExpression";
import CuratedSearchCiroInternalJobTitleSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalJobTitleSection";
import CiroTextInput from "../../shared/CiroTextInput";
import CuratedSearchCardHeader from "../CuratedSearchCard/CuratedSearchCardHeader";

const INTERNAL_SEARCH_FILTER_MAX_LENGTH = 255;

const filterPastMaxLength = (
  filter: { label: string; id?: string | number }[],
) => {
  return (
    filter
      .map((f) => f.id || f.label)
      .map((f) => `"${f}"`)
      .join(" OR ").length > INTERNAL_SEARCH_FILTER_MAX_LENGTH
  );
};

const CuratedInternalSearchFilterCard = ({
  currentFilters,
  setCurrentFilters,
  onSave,
  curatedSearchId,
  additionalFiltering,
  setAdditionalFiltering,
  hideHeader = false,
}: {
  currentFilters: InternalCuratedSearchFilters | null;
  setCurrentFilters: (filters: InternalCuratedSearchFilters) => void;
  onSave: () => Promise<void>;
  curatedSearchId: number | null;
  additionalFiltering: string[];
  setAdditionalFiltering: (additionalFiltering: string[]) => void;
  hideHeader?: boolean;
}) => {
  const { upsertAutopilotCuratedSearchLoading } = useContext(
    AutopilotCuratedSearchContext,
  );

  // Track whether validation errors should be shown
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const hasJobTitleFilterError =
    (currentFilters?.jobTitles?.length || 0) >
      INTERNAL_SEARCH_FILTER_MAX_LENGTH ||
    (Boolean(currentFilters?.jobTitles) &&
      !isValidBooleanExpression(currentFilters?.jobTitles || ""));

  const hasIndustryFilterError = filterPastMaxLength(
    currentFilters?.industries || [],
  );

  const hasGeographyFilterError = filterPastMaxLength(
    currentFilters?.geographies || [],
  );

  const hasCompanyHqFilterError = filterPastMaxLength(
    currentFilters?.companyHQLocations || [],
  );

  const hasCompanyKeyWordsFilterError =
    (currentFilters?.companyKeywords?.length || 0) >
      INTERNAL_SEARCH_FILTER_MAX_LENGTH ||
    (Boolean(currentFilters?.companyKeywords) &&
      !isValidBooleanExpression(currentFilters?.companyKeywords || ""));

  const hasProfileSummaryKeywordsFilterError =
    (currentFilters?.profileSummaryKeywords?.length || 0) >
      INTERNAL_SEARCH_FILTER_MAX_LENGTH ||
    (Boolean(currentFilters?.profileSummaryKeywords) &&
      !isValidBooleanExpression(currentFilters?.profileSummaryKeywords || ""));

  // Validate scoring criteria
  const getScoringCriteriaErrors = (filters: string[]) =>
    filters.map((filter) => {
      if (!filter.trim()) {
        return "Scoring criteria cannot be empty";
      }
      if (filter.length > INTERNAL_SEARCH_FILTER_MAX_LENGTH) {
        return "Scoring criteria is too long";
      }
      return null;
    });

  const hasScoringCriteriaErrors = getScoringCriteriaErrors(
    additionalFiltering,
  ).some((error) => error !== null);

  return (
    <div>
      {!hideHeader && (
        <CuratedSearchCardHeader
          headerText="Filters"
          headerIcon={
            <AdjustmentsVerticalIcon className="h-5 w-5 text-orange-500" />
          }
        />
      )}
      <div
        className={classNames("grid", "grid-cols-1", "xl:grid-cols-2", "gap-2")}
      >
        <CuratedSearchCiroInternalIndustrySection
          defaultOpen={false}
          hasError={hasIndustryFilterError}
          currentIndustryFilter={
            (currentFilters?.industries ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentIndustryFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              industries: v,
            });
          }}
        />
        <CuratedSearchCiroInternalJobTitleSection
          defaultOpen={false}
          hasError={hasJobTitleFilterError}
          jobTitleFilter={currentFilters?.jobTitles || null}
          setJobTitleFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              jobTitles: v,
            });
          }}
        />
        <CuratedSearchCiroInternalCompanyKeyWordsSection
          hasError={hasCompanyKeyWordsFilterError}
          currentKeyWordsFilter={currentFilters?.companyKeywords || null}
          setCurrentKeyWordsFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              companyKeywords: v,
            });
          }}
        />
        {/* TODO: Add back once we figure out latency with Proxycurl CIR-3434 */}
        {/* <CuratedSearchCiroInternalProfileSummaryKeywordsSection
          hasError={hasProfileSummaryKeywordsFilterError}
          currentProfileSummaryKeywords={currentFilters?.profileSummaryKeywords ?? null}
          setCurrentProfileSummaryKeywords={(v) => {
            setCurrentFilters({
              ...currentFilters,
              profileSummaryKeywords: v,
            });
          }}
        /> */}
        <CuratedSearchCiroInternalGeographySection
          defaultOpen={false}
          hasError={hasGeographyFilterError}
          currentGeographyFilter={
            (currentFilters?.geographies ?? []) as {
              label: string;
              id: string;
            }[]
          }
          setCurrentGeographyFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              geographies: v,
            });
          }}
        />
        <CuratedSearchCiroInternalCompanyHqSection
          defaultOpen={false}
          currentCompanyHqFilter={
            (currentFilters?.companyHQLocations ?? []) as {
              label: string;
              id: string;
            }[]
          }
          hasError={hasCompanyHqFilterError}
          setCurrentCompanyHqFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              companyHQLocations: v,
            });
          }}
        />
        <CuratedSearchFilterCurrentCompanySection
          defaultOpen={false}
          currentCompanyFilter={
            (currentFilters?.companyNames ?? []) as {
              label: string;
            }[]
          }
          setCurrentCompanyFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              companyNames: v,
            });
          }}
        />
        <CuratedSearchCiroInternalCompanyHeadCountSection
          currentCompanyHeadCountFilter={
            currentFilters?.companyHeadCountRange ?? null
          }
          setCurrentCompanyHeadCountFilter={(v) => {
            setCurrentFilters({
              ...(currentFilters || {}),
              companyHeadCountRange: v,
            });
          }}
        />
      </div>
      <div className={classNames("px-2", "pt-6", "flex", "flex-col", "gap-2")}>
        <div className={classNames("flex", "items-center", "gap-2")}>
          <SparklesIcon className="h-6 w-6 text-orange-500" />
          <div>
            <div className={classNames("font-semibold")}>Scoring criteria</div>
          </div>
        </div>
        <div>
          {additionalFiltering.length > 0 && (
            <div>
              {additionalFiltering.map((filter, idx) => (
                <div
                  key={`filter-container-${idx}`}
                  className={classNames(
                    "flex",
                    "items-center",
                    "gap-4",
                    "py-2",
                    "px-1",
                    "text-sm",
                  )}
                >
                  <CiroTextInput
                    key={idx}
                    wrapperClassName={classNames("w-full")}
                    value={filter}
                    error={
                      showValidationErrors
                        ? getScoringCriteriaErrors(additionalFiltering)[idx]
                        : null
                    }
                    onChange={(e) => {
                      const newAdditionalFiltering = [...additionalFiltering];
                      newAdditionalFiltering[idx] = e.target.value;
                      setAdditionalFiltering(newAdditionalFiltering);

                      // Clear validation errors if all criteria are valid after editing
                      if (showValidationErrors) {
                        const newErrors = getScoringCriteriaErrors(
                          newAdditionalFiltering,
                        );

                        if (!newErrors.some((error) => error !== null)) {
                          setShowValidationErrors(false);
                        }
                      }
                    }}
                  />
                  <CiroButton
                    analyticsField={"Removing additional filter"}
                    style={CiroButtonStyleEnum.UNSTYLED}
                    onClick={() => {
                      const newAdditionalFiltering = [...additionalFiltering];
                      newAdditionalFiltering.splice(idx, 1);
                      setAdditionalFiltering(newAdditionalFiltering);
                    }}
                  >
                    <XMarkIcon
                      className={classNames("w-5", "text-neutral-500")}
                    />
                  </CiroButton>
                </div>
              ))}
            </div>
          )}

          <div className={classNames("mt-2", "ml-1")}>
            <CiroButton
              analyticsField={"Add scoring criteria"}
              style={CiroButtonStyleEnum.PRIMARY}
              onClick={() => {
                const newAdditionalFiltering = [...additionalFiltering, ""];
                setAdditionalFiltering(newAdditionalFiltering);
              }}
            >
              <div className={classNames("flex", "items-center", "gap-2")}>
                <span>+</span>
                <span>Add criterion</span>
              </div>
            </CiroButton>
          </div>
        </div>
      </div>
      <div className={classNames("flex", "justify-end", "gap-2", "pt-2")}>
        <CiroButton
          analyticsField="Save Internal Ciro Database Filters"
          style={CiroButtonStyleEnum.LOUD}
          disabled={
            !hasFiltersApplied({ filters: currentFilters }) ||
            hasJobTitleFilterError ||
            hasIndustryFilterError ||
            hasGeographyFilterError ||
            hasCompanyHqFilterError ||
            hasCompanyKeyWordsFilterError ||
            hasProfileSummaryKeywordsFilterError ||
            upsertAutopilotCuratedSearchLoading
            // Note: hasScoringCriteriaErrors is removed to allow clicking with scoring criteria errors
          }
          onClick={async () => {
            if (!currentFilters) {
              toast.error("No filters applied");
              return;
            }
            if (hasScoringCriteriaErrors) {
              setShowValidationErrors(true);
              return;
            }
            await onSave();
          }}
        >
          {Boolean(curatedSearchId) ? "Regenerate preview" : "Preview results"}
        </CiroButton>
      </div>
    </div>
  );
};

export default CuratedInternalSearchFilterCard;
